import { createClient } from '@supabase/supabase-js';
import type { SupportedStorage } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY is not defined in environment.');
}

// Parses the JWT to reconstruct the User object.
// Needed because the portal strips the `user` field from the cookie to stay within 4KB.
const parseJwt = (token: string) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(
      decodeURIComponent(
        window.atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
    );
  } catch {
    return null;
  }
};

const getCookieDomain = (): string => {
  const h = window.location.hostname;
  if (h.endsWith('.localtest.me') || h === 'localtest.me') return '.localtest.me';
  if (h.endsWith('.sakura3.id') || h === 'sakura3.id') return '.sakura3.id';
  if (h.endsWith('.sr3.my.id') || h === 'sr3.my.id') return '.sr3.my.id';
  if (h.endsWith('.veryresto.com') || h === 'veryresto.com') return '.veryresto.com';
  return h;
};

const isLocalHostOrIP = (h: string) =>
  h === 'localhost' || h === '127.0.0.1' || /^\d{1,3}(\.\d{1,3}){3}$/.test(h);

class CookieStorage implements SupportedStorage {
  private domain = getCookieDomain();
  private isLocalOrIP = isLocalHostOrIP(this.domain);

  getItem(key: string): string | null {
    const parts = `; ${document.cookie}`.split(`; ${key}=`);
    if (parts.length !== 2) return null;
    const raw = decodeURIComponent(parts.pop()!.split(';').shift()!);
    try {
      const session = JSON.parse(raw);
      // Reconstruct user from JWT claims if missing (portal strips it to save cookie space)
      if (session?.access_token && !session.user) {
        const payload = parseJwt(session.access_token);
        if (payload) {
          session.user = {
            id: payload.sub,
            aud: payload.aud,
            role: payload.role,
            email: payload.email,
            phone: payload.phone,
            app_metadata: payload.app_metadata || {},
            user_metadata: payload.user_metadata || {},
            created_at: payload.created_at || new Date().toISOString(),
          };
          return JSON.stringify(session);
        }
      }
    } catch {
      /* non-session value */
    }
    return raw;
  }

  setItem(key: string, value: string): void {
    let stored = value;
    try {
      const session = JSON.parse(value);
      if (session?.access_token && session?.refresh_token) {
        // Strip user object — keep cookie under 4KB
        stored = JSON.stringify({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          expires_at: session.expires_at,
          expires_in: session.expires_in,
          token_type: session.token_type || 'bearer',
        });
      }
    } catch {
      /* non-session, store as-is */
    }
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    const domainAttr = this.isLocalOrIP ? '' : `;domain=${this.domain}`;
    const secure = window.location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `${key}=${encodeURIComponent(stored)};expires=${expires}${domainAttr};path=/;SameSite=Lax${secure}`;
  }

  removeItem(key: string): void {
    const domainAttr = this.isLocalOrIP ? '' : `;domain=${this.domain}`;
    const secure = window.location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 UTC${domainAttr};path=/;SameSite=Lax${secure}`;
  }
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      storageKey: 'sakura3-auth', // ⚠️ Must match the portal's storage key exactly
      storage: new CookieStorage(),
      persistSession: true,
      autoRefreshToken: true,
    },
  }
);
